/**
 * The object panel's data.
 *
 * There is NO push feed for this panel. `ReportEnabledChange`
 * (`layer3/Executive.cpp:313-322`) only invokes `G->enabledCallback` under
 * `_PYMOL_LIB`, and the panel itself is a C++ `Block::draw` surface
 * (`struct CExecutive : public Block`, `layer3/ExecutiveDef.h:54`) redrawn from
 * the live `Spec` list — nothing in the Python build announces a change. So this
 * store POLLS, at the plan §1.5 cadence (30 Hz focused / 4 Hz hidden), through
 * exactly two calls per pass:
 *
 *     cmd.get_names('public', 0)   -> panel order        (querying.py:1155-1199)
 *     cmd.get_vis()                -> enabled/reps/color (viewing.py:899-901,
 *                                     C: ExecutiveGetVisAsPyDict :4481-4512)
 *
 * Measured against the running bridge: 0.22 ms per round trip, so a 30 Hz poll
 * of both costs ~1.3 % of one engine tick. `get_type` and `count_atoms` are
 * per-name and CACHED — `count_atoms` is 5,902 us at 500k atoms and is banned
 * from the hot path (plan §6 WP-10).
 *
 * When WP-12's `objects` topic starts pushing `ObjectsPayload`, call
 * `adoptTopic()`: the store switches source and the poller stops. Both paths
 * produce the same rows.
 *
 * THE GAP THAT WAS "deliberately not faked" IS NOW CLOSED. Group nesting,
 * `PanelRec.nest_level` and `ObjectGroup::OpenOrClosed` live only in C++
 * (`layer3/ExecutiveDef.h:20-31`), but they ARE all present in
 * `cmd.get_session(partial=1)['names']` — `rec[6]` is `group_name`, `rec[4]` is
 * the `cObject_t` enum and `rec[5][1]` is `OpenOrClosed`. That call was assumed
 * "far too heavy to poll" (internal-gui.md §11); MEASURED on this build it is
 * **0.036 ms**, because `partial=1` skips coordinates entirely
 * (`Executive.cpp:5506-5510`). `bridge/tenmol_bridge/panels/objects.py` is
 * built on it and answers a whole panel snapshot in 0.09 ms.
 *
 * So there are three row sources now, in decreasing fidelity:
 *
 *   `panel`  `cmd.tenmol_objects('snapshot')` — real `PanelListGroup` order,
 *            real nest levels, real open/closed, captions, name colours.
 *   `topic`  a pushed `ObjectsPayload`.
 *   `poll`   the two-call fallback in `objectsSource.ts`, whose nesting is
 *            INFERRED from dotted names and flagged `nestInferred`.
 *
 * `applyRows` refuses to let a `poll` pass overwrite a live `panel` snapshot,
 * so the fallback degrading the good data is structurally impossible rather
 * than a matter of call ordering.
 */

import type {
  PanelMenuNode,
  PanelMenuPayload,
  ObjectRow,
  PanelSettings,
  PanelSnapshot,
  PymolObjectType,
} from '@tenmol/protocol';
import { createStore, type Store } from './createStore';

/** `cmd.get_vis()` value: `[visible, [], repIndices | None, colorIndex | None]`. */
export type VisEntry = [number, unknown[], number[] | null, number | null];
/** Name -> its `cmd.get_vis()` entry, the whole result of one call. */
export type VisMap = Record<string, VisEntry>;

/** A row as the panel renders it: the wire row plus what the client derives. */
export interface PanelRow extends ObjectRow {
  isGroup: boolean;
  /** Enabled, but an ancestor group is disabled (`layer3/Executive.cpp:16392-16406`). */
  cloaked: boolean;
  /** The synthetic `all` row (`cExecAll`), which is not in `get_names`. */
  isAll: boolean;
  /** `cmd.count_atoms(name)`, cached; null until fetched or not applicable. */
  atoms: number | null;
  /** True when `nest`/`group` were derived from the name, not read from PyMOL. */
  nestInferred: boolean;
  /** `ObjectGroup::OpenOrClosed`; only meaningful with the `panel` source. */
  isOpen?: boolean;
  /** `getNameColor` (`Executive.cpp:16117-16165`) result, 0..1 RGB. */
  nameColor?: readonly number[];
}

/** Which mechanism supplied a set of rows to the store. */
export type RowSource = 'none' | 'poll' | 'topic';

/** Which feed actually produced `rows`; see the file header. */
export type RowFeed = 'none' | 'poll' | 'topic' | 'panel';

/** How long a `panel` snapshot keeps the two-call poll locked out. */
export const PANEL_FEED_TTL_MS = 3000;

/** `get_op_cnt()` default (`layer3/Executive.cpp:1757-1765`). */
export const DEFAULT_PANEL: Omit<PanelSnapshot, 'rows'> = {
  opCount: 5,
  buttonMode: '',
  ops: ['A', 'S', 'H', 'L', 'C'],
  settings: {
    group_full_member_names: 0,
    group_arrow_prefix: 0,
    internal_gui_name_color_mode: 0,
    internal_gui_control_size: 18,
    internal_gui_width: 220,
    hide_underscore_names: 1,
  },
};

/** The object panel store's full state: rows, their provenance, and panel-wide settings. */
export interface ObjectsState {
  rows: readonly PanelRow[];
  source: RowSource;
  feed: RowFeed;
  /** Bumped whenever the row set (not just the flags) changes. */
  generation: number;
  /** Epoch ms of the last successful snapshot. */
  updatedAt: number | null;
  /** Epoch ms of the last `panel` snapshot; drives the poll lock-out. */
  panelAt: number | null;
  lastError: string | null;
  /** Panel-wide state from the snapshot: op count, button mode, settings. */
  panel: Omit<PanelSnapshot, 'rows'>;
  /** Group names whose children the user collapsed WITHOUT a panel feed.
   *  With the panel feed, collapse is `ObjectGroup::OpenOrClosed` on the
   *  server and this list is unused. */
  collapsed: readonly string[];
  /** `name op` -> the menu the bridge answered, cached until invalidated. */
  menus: Readonly<Record<string, PanelMenuPayload>>;
}

/** The object panel store: state plus the mutators that feed rows and cache menus. */
export interface ObjectsStore extends Store<ObjectsState> {
  applyRows(rows: PanelRow[], source: RowSource): void;
  /** The `panels/objects.py` snapshot: rows plus panel-wide state. */
  applySnapshot(snapshot: PanelSnapshot): void;
  /** Stop locking out the poll (the panel endpoint went away / unmounted). */
  releasePanelFeed(): void;
  setError(message: string | null): void;
  toggleCollapsed(name: string): void;
  cacheMenu(payload: PanelMenuPayload): void;
  /** Replace one lazily-expanded submenu in place, by its `path`. */
  expandMenu(name: string, op: string, path: readonly number[], items: PanelMenuNode[]): void;
  clearMenus(): void;
}

/** Cache key for `ObjectsState.menus`. */
export function menuKey(name: string, op: string): string {
  return `${name} ${op.toUpperCase()}`;
}

/** Construct the object panel store, wired with its poll/topic/panel merge rules. */
export function createObjectsStore(): ObjectsStore {
  const store = createStore<ObjectsState>({
    rows: [],
    source: 'none',
    feed: 'none',
    generation: 0,
    updatedAt: null,
    panelAt: null,
    lastError: null,
    panel: DEFAULT_PANEL,
    collapsed: [],
    menus: {},
  });

  function put(rows: PanelRow[], source: RowSource, feed: RowFeed, extra?: Partial<ObjectsState>) {
    store.set((state) => {
      const same = rowsEqual(state.rows, rows);
      if (same && state.source === source && state.feed === feed && state.lastError === null) {
        // Nothing changed: keep the old array identity so React does not
        // re-render the panel 30 times a second for no reason.
        return { updatedAt: Date.now(), ...extra };
      }
      return {
        rows,
        source,
        feed,
        updatedAt: Date.now(),
        lastError: null,
        generation: same ? state.generation : state.generation + 1,
        ...extra,
      };
    });
  }

  return {
    ...store,

    applyRows(rows, source) {
      // A `poll` pass must never clobber a live `panel` snapshot: its nesting
      // is inferred and its captions are absent, so accepting it would be a
      // visible downgrade every time the two feeds raced.
      const state = store.get();
      if (
        source === 'poll' &&
        state.feed === 'panel' &&
        state.panelAt !== null &&
        Date.now() - state.panelAt < PANEL_FEED_TTL_MS
      ) {
        return;
      }
      put(rows, source, source === 'topic' ? 'topic' : 'poll');
    },

    applySnapshot(snapshot) {
      const rows = rowsFromSnapshot(snapshot);
      // `source: 'topic'` is what `objectsSource.poll()` tests to idle itself
      // (`if (store.get().source === 'topic') return`), so the 30 Hz two-call
      // poll costs nothing while the panel endpoint is driving. `feed` is the
      // honest answer to "where did these rows come from".
      put(rows, 'topic', 'panel', {
        panelAt: Date.now(),
        panel: {
          opCount: snapshot.opCount,
          buttonMode: snapshot.buttonMode,
          ops: snapshot.ops,
          settings: snapshot.settings,
        },
      });
    },

    releasePanelFeed() {
      if (store.get().feed !== 'panel') return;
      store.set({ feed: 'poll', source: 'poll', panelAt: null });
    },

    setError(message) {
      store.set({ lastError: message });
    },

    toggleCollapsed(name) {
      store.set((state) => ({
        collapsed: state.collapsed.includes(name)
          ? state.collapsed.filter((n) => n !== name)
          : [...state.collapsed, name],
      }));
    },

    cacheMenu(payload) {
      store.set((state) => ({
        menus: { ...state.menus, [menuKey(payload.name, payload.op)]: payload },
      }));
    },

    expandMenu(name, op, path, items) {
      store.set((state) => {
        const key = menuKey(name, op);
        const current = state.menus[key];
        if (!current) return {};
        return {
          menus: {
            ...state.menus,
            [key]: { ...current, items: replaceAtPath(current.items, path, items) },
          },
        };
      });
    },

    clearMenus() {
      if (Object.keys(store.get().menus).length === 0) return;
      store.set({ menus: {} });
    },
  };
}

/** Immutably swap in a resolved submenu at `path` (see `PanelMenuNode.path`). */
export function replaceAtPath(
  items: readonly PanelMenuNode[],
  path: readonly number[],
  resolved: PanelMenuNode[],
): PanelMenuNode[] {
  if (path.length === 0) return resolved;
  const [head, ...rest] = path;
  return items.map((node, index) => {
    if (index !== head) return node;
    if (rest.length === 0) return { ...node, items: resolved, lazy: false };
    return { ...node, items: replaceAtPath(node.items ?? [], rest, resolved) };
  });
}

/**
 * `panels/objects.py`'s rows -> `PanelRow`. The only thing computed here is
 * `cloaked`, because it depends on ancestors and `cmd.get_vis()` never reports
 * it (`layer3/Executive.cpp:16443-16463`).
 */
export function rowsFromSnapshot(snapshot: PanelSnapshot): PanelRow[] {
  const rows: PanelRow[] = (snapshot.rows ?? []).map((row) => ({
    name: row.name,
    type: (row.isAll ? 'object:group' : row.type) as PymolObjectType,
    enabled: row.enabled,
    group: row.group,
    nest: row.nest,
    reps: row.reps,
    color: row.color,
    caption: row.caption,
    isGroup: row.isGroup,
    cloaked: false,
    isAll: row.isAll,
    atoms: null,
    nestInferred: false,
    isOpen: row.isOpen,
    ...(row.nameColor ? { nameColor: row.nameColor } : {}),
  }));
  markCloaked(rows);
  return rows;
}

/**
 * "Cloaked": enabled, but an ancestor group is disabled
 * (`layer3/Executive.cpp:16443-16463`). Walks the real `group` chain, so it is
 * correct for the panel feed and best-effort for the inferred one.
 */
export function markCloaked(rows: PanelRow[]): void {
  const enabledByName = new Map(rows.map((row) => [row.name, row.enabled]));
  const groupByName = new Map(rows.map((row) => [row.name, row.group]));
  for (const row of rows) {
    if (!row.enabled || row.group === '') continue;
    let parent: string | undefined = row.group;
    const seen = new Set<string>();
    while (parent && !seen.has(parent)) {
      seen.add(parent);
      if (enabledByName.get(parent) === false) {
        row.cloaked = true;
        break;
      }
      const next = groupByName.get(parent);
      if (next !== undefined) {
        parent = next === '' ? undefined : next;
      } else {
        const dot = parent.lastIndexOf('.');
        parent = dot > 0 ? parent.slice(0, dot) : undefined;
      }
    }
  }
}

/* ------------------------------------------------------------------ *
 * `\RGB` text colour escapes (`layer1/Text.cpp:507-548`)
 * ------------------------------------------------------------------ */

/** A run of menu/caption text with the colour that was in force for it. */
export interface ColoredSpan {
  text: string;
  /** CSS colour, or null for "the default colour of this surface". */
  color: string | null;
}

/** `\RGB`, digits 0-9, exactly as `TextSetColorFromCode` decodes them. */
export function swatchFromCode(code: string): string {
  const value = (index: number) => Math.round((Number(code[index] ?? '0') / 9) * 255);
  return `rgb(${value(0)}, ${value(1)}, ${value(2)})`;
}

/**
 * Split a PyMOL string on its `\RGB` escapes.
 *
 * `TextSetColorFromCode` (`layer1/Text.cpp:507-548`): a backslash followed by
 * three characters. `\---` resets to the default colour; otherwise each of the
 * three must be a digit 0-9 and becomes one channel scaled by 1/9. Anything
 * else is literal text — a backslash in `cmd.label("x","'%1.2f'%b")` must
 * survive untouched.
 */
export function parseColorCodes(text: string): ColoredSpan[] {
  const spans: ColoredSpan[] = [];
  let color: string | null = null;
  let buffer = '';
  for (let i = 0; i < text.length; ) {
    if (text[i] === '\\' && i + 3 < text.length) {
      const code = text.slice(i + 1, i + 4);
      if (code === '---' || /^[0-9]{3}$/.test(code)) {
        if (buffer) spans.push({ text: buffer, color });
        buffer = '';
        color = code === '---' ? null : swatchFromCode(code);
        i += 4;
        continue;
      }
    }
    buffer += text[i];
    i += 1;
  }
  if (buffer) spans.push({ text: buffer, color });
  return spans;
}

/** The same string with every `\RGB` escape removed (for titles, aria-labels). */
export function stripColorCodes(text: string): string {
  return parseColorCodes(text)
    .map((span) => span.text)
    .join('');
}

/* ------------------------------------------------------------------ *
 * Row construction (pure — unit tested)
 * ------------------------------------------------------------------ */

/** The four cached inputs `buildRows` needs to assemble the poll-fallback rows. */
export interface BuildRowsInput {
  /** `cmd.get_names('public', 0)`, in panel order. */
  names: readonly string[];
  /** `cmd.get_vis()`. */
  vis: VisMap;
  /** name -> `cmd.get_type(name)`, cached across polls. */
  types: ReadonlyMap<string, string>;
  /** name -> `cmd.count_atoms(name)`, cached; optional. */
  atoms?: ReadonlyMap<string, number>;
}

/** Assemble the two-call poll fallback's rows, inferring group nesting from dotted names. */
export function buildRows(input: BuildRowsInput): PanelRow[] {
  const { names, vis, types, atoms } = input;

  const groupNames = new Set(names.filter((name) => types.get(name) === 'object:group'));

  const allVis = vis['all'];
  const rows: PanelRow[] = [
    {
      name: 'all',
      type: 'object:group',
      enabled: allVis ? allVis[0] !== 0 : true,
      group: '',
      nest: 0,
      reps: 0,
      color: null,
      caption: '',
      isGroup: false,
      cloaked: false,
      isAll: true,
      atoms: atoms?.get('all') ?? null,
      nestInferred: false,
    },
  ];

  for (const name of names) {
    const entry = vis[name];
    const type = (types.get(name) ?? 'object:molecule') as PymolObjectType;
    const group = inferGroup(name, groupNames);
    rows.push({
      name,
      type,
      enabled: entry ? entry[0] !== 0 : false,
      group,
      nest: group === '' ? 0 : group.split('.').length,
      reps: entry ? bitmaskFromRepIndices(entry[2]) : 0,
      color: entry ? entry[3] : null,
      caption: '',
      isGroup: groupNames.has(name),
      cloaked: false,
      isAll: false,
      atoms: atoms?.get(name) ?? null,
      nestInferred: group !== '',
    });
  }

  markCloaked(rows);
  return rows;
}

/**
 * `cmd.get_vis()` element 2 is the rep INDEX list built by
 * `getRepArrayFromBitmask` (`layer3/Executive.cpp:4514-4525`); `ObjectRow.reps`
 * is the bitmask it came from. Note what this is worth: for a molecule this is
 * the OBJECT-level `visRep`, which is all-on by default — per-atom reps are
 * invisible to it. Plan §1.5 proved it: `show spheres, m and name CA` leaves
 * `get_vis()` byte-identical while 574 atoms carry the rep. It is kept for
 * non-molecular objects (maps, meshes, surfaces), where it is meaningful.
 */
export function bitmaskFromRepIndices(indices: number[] | null): number {
  if (!indices) return 0;
  let mask = 0;
  for (const index of indices) {
    if (index >= 0 && index < 31) mask |= 1 << index;
  }
  return mask;
}

/** Longest known group name that is a dotted prefix of `name`. */
function inferGroup(name: string, groups: ReadonlySet<string>): string {
  let best = '';
  for (let dot = name.lastIndexOf('.'); dot > 0; dot = name.lastIndexOf('.', dot - 1)) {
    const candidate = name.slice(0, dot);
    if (groups.has(candidate) && candidate.length > best.length) best = candidate;
  }
  return best;
}

/**
 * True when two row lists are equivalent for rendering. Compares every field the
 * panel draws, so a 30 Hz poll that finds nothing new produces zero renders.
 */
export function rowsEqual(a: readonly PanelRow[], b: readonly PanelRow[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (!x || !y) return false;
    if (
      x.name !== y.name ||
      x.type !== y.type ||
      x.enabled !== y.enabled ||
      x.group !== y.group ||
      x.nest !== y.nest ||
      x.reps !== y.reps ||
      x.color !== y.color ||
      x.caption !== y.caption ||
      x.isGroup !== y.isGroup ||
      x.cloaked !== y.cloaked ||
      x.atoms !== y.atoms
    ) {
      return false;
    }
  }
  return true;
}

/** Rows the panel actually shows: children of a collapsed group are hidden. */
export function visibleRows(
  rows: readonly PanelRow[],
  collapsed: readonly string[],
): readonly PanelRow[] {
  if (collapsed.length === 0) return rows;
  const hidden = new Set(collapsed);
  return rows.filter((row) => {
    let parent = row.group;
    while (parent) {
      if (hidden.has(parent)) return false;
      const dot = parent.lastIndexOf('.');
      parent = dot > 0 ? parent.slice(0, dot) : '';
    }
    return true;
  });
}

/**
 * The name PyMOL draws (`layer3/Executive.cpp:16475-16513`).
 *
 * The prefix is stripped ONLY when `group_full_member_names` is 0 **and** the
 * name literally starts with the group's name followed by a `.` — the C loop
 * walks both strings and requires `(*p) && (!*q) && (*p == '.')`. That test
 * matters: `cmd.group('g1', 'ala trp')` produces members whose names are NOT
 * dot-prefixed at all, and blindly slicing `group.length + 1` off them (which
 * is what this function used to do) truncated `ala` to the empty string.
 *
 * When the prefix IS hidden and `group_arrow_prefix` is set, PyMOL draws `^`
 * and `|` overstruck in the same cell (`:16501-16508`); `^|` is the closest
 * honest rendering in DOM text.
 *
 * Selections are wrapped in parentheses (`:16491-16495`, `:16517-16521`).
 */
export function displayName(row: PanelRow, settings?: Partial<PanelSettings>): string {
  const fullNames = (settings?.group_full_member_names ?? 0) !== 0;
  const arrows = (settings?.group_arrow_prefix ?? 0) !== 0;
  let text = row.name;
  let hiddenPrefix = false;
  if (!fullNames && row.group !== '' && row.name.startsWith(`${row.group}.`)) {
    text = row.name.slice(row.group.length + 1);
    hiddenPrefix = true;
  }
  if (hiddenPrefix && arrows) text = `^|${text}`;
  return row.type === 'selection' ? `(${text})` : text;
}
